import type { ChartDropPlace } from '../../model/chartDrop';

/** Прямоугольник на странице в координатах окна. */
type PageRect = Pick<DOMRectReadOnly, 'left' | 'top' | 'right' | 'bottom' | 'width' | 'height'>;

/** Ячейка диаграммы сетки и её место на странице. */
type ChartDropCell = {
  chartId: string;
  rect: PageRect;
};

/** Колонка строки глазами перетаскивания: её ячейки сверху вниз. */
export type ChartDropColumn = {
  cells: ChartDropCell[];
};

/** Строка сетки глазами перетаскивания: её колонки по порядку и незанятый конец в колонках раскладки. */
export type ChartDropRow = {
  columns: ChartDropColumn[];
  freeColumns: number;
};

/** Раскладка сетки на момент перетаскивания: из неё и выводится место под курсором. */
export type ChartDropLayout = {
  gridRect: PageRect;
  rows: ChartDropRow[];
  /** Перетаскиваемая диаграмма: её ячейка на прежнем месте и занятые ею колонки раскладки */
  source: ChartDropCell & { columns: number };
  /** Отступ карточки от краёв её ячейки — половина зазора между диаграммами по ширине и по высоте */
  cellGutter: { x: number; y: number };
};

/**
 * Индикатор места в координатах сетки: черта между диаграммами строки, черта между строками либо
 * контур диаграммы на пустом месте строки.
 */
export type ChartDropIndicatorShape =
  | { kind: 'vertical'; left: number; top: number; height: number }
  | { kind: 'horizontal'; left: number; top: number; width: number }
  | { kind: 'outline'; left: number; top: number; width: number; height: number };

/** Место под курсором: куда встанет диаграмма и чем это место показано. */
export type ChartDropTarget = {
  place: ChartDropPlace;
  indicator: ChartDropIndicatorShape;
};

/**
 * Запас, на который промежуток между строками заходит в сами строки: попасть в промежуток не
 * должно требовать точности, а ширины видимого зазора для этого мало.
 */
const ROW_GAP_CATCH = 8;

/**
 * Полоса у верхнего и нижнего края диаграммы, отпускание в которой ставит перетаскиваемую над ней или
 * под ней, в её колонку: не шире этого числа пикселей и не шире четверти высоты диаграммы, чтобы
 * середина карточки оставалась местом «перед» и «после».
 */
const STACK_EDGE_CATCH = 40;
const STACK_EDGE_SHARE = 0.25;

/** Ячейки строки в порядке состава: колонка за колонкой, в колонке — сверху вниз. */
const rowCells = (row: ChartDropRow): ChartDropCell[] => row.columns.flatMap((column) => column.cells);

/** Вертикальные границы строки — по самой высокой из её колонок. */
const rowBounds = (row: ChartDropRow) => {
  const cells = rowCells(row);
  return {
    top: Math.min(...cells.map((cell) => cell.rect.top)),
    bottom: Math.max(...cells.map((cell) => cell.rect.bottom)),
  };
};

/** Границы колонки по ширине: у её диаграмм ширина одна. */
const columnBounds = (column: ChartDropColumn) => ({
  left: Math.min(...column.cells.map((cell) => cell.rect.left)),
  right: Math.max(...column.cells.map((cell) => cell.rect.right)),
});

/**
 * Место перетаскиваемой диаграммы под курсором `pointer` и его индикатор; курсор вне сетки и
 * промежутка над ней или над самой перетаскиваемой диаграммой — `null`.
 *
 * Промежуток между строками перекрывает края строк запасом и берёт всё, что между ними, — в том числе
 * развёрнутые под строкой свойства диаграммы. Так же, с запасом, ловится промежуток над первой
 * строкой — между панелью дашборда и сеткой: отпущенная в нём диаграмма открывает новую первую
 * строку. Зазор над сеткой страница держит тем же, что и между строками, поэтому и полоса над первой
 * строкой отсчитывается от него. Внутри строки место выбирается по колонке и ячейке под
 * курсором: у верхнего или нижнего края диаграммы — над ней или под ней в её колонку, иначе — у
 * ближнего к курсору края колонки, а правее последней — на пустом месте строки. Ячейка включает свою
 * половину зазора, поэтому промежутки между диаграммами строки и колонки ловятся с запасом сами собой.
 *
 * Пустое место строки показано контуром перетаскиваемой диаграммы там, где она встанет: следом за
 * колонками строки, а если ей не хватает ширины — в начале следующей строки. Сама она в счёт ширины
 * строки не идёт, когда стоит в ней одна в своей колонке: её место освобождается переносом.
 */
export const chartDropTarget = (pointer: { x: number; y: number }, layout: ChartDropLayout): ChartDropTarget | null => {
  const { gridRect, rows, source, cellGutter } = layout;
  if (pointer.x < gridRect.left || pointer.x > gridRect.right) return null;
  if (pointer.y > gridRect.bottom + ROW_GAP_CATCH || rows.length === 0) return null;

  const toGridX = (x: number) => x - gridRect.left;
  const toGridY = (y: number) => y - gridRect.top;

  // Промежуток над первой строкой: от панели дашборда с запасом до запаса внутри первой строки
  const firstTop = rowBounds(rows[0]).top;
  if (pointer.y < firstTop + ROW_GAP_CATCH) {
    if (pointer.y < firstTop - cellGutter.y * 2 - ROW_GAP_CATCH) return null;
    return {
      place: { kind: 'firstRow' },
      indicator: {
        kind: 'horizontal',
        left: cellGutter.x,
        top: toGridY(firstTop - cellGutter.y),
        width: gridRect.width - cellGutter.x * 2,
      },
    };
  }

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex];
    const bounds = rowBounds(row);
    const nextRow = rows[rowIndex + 1];
    const nextTop = nextRow ? rowBounds(nextRow).top : null;
    const others = rowCells(row).filter((cell) => cell.chartId !== source.chartId);
    const lastOther = others.at(-1);

    if (pointer.y < bounds.bottom - ROW_GAP_CATCH) {
      const column = row.columns.find((item) => pointer.x < columnBounds(item).right);
      if (column) {
        const columnOthers = column.cells.filter((cell) => cell.chartId !== source.chartId);
        const firstOther = columnOthers[0];
        const lastColumnOther = columnOthers.at(-1);
        if (!firstOther || !lastColumnOther) return null;
        const cell = column.cells.find((item) => pointer.y < item.rect.bottom + cellGutter.y) ?? column.cells.at(-1);
        if (!cell || cell.chartId === source.chartId) return null;
        const { left, right } = columnBounds(column);

        const edgeCatch = Math.min(STACK_EDGE_CATCH, cell.rect.height * STACK_EDGE_SHARE);
        const isAbove = pointer.y < cell.rect.top + edgeCatch;
        if (isAbove || pointer.y > cell.rect.bottom - edgeCatch) {
          return {
            place: { kind: isAbove ? 'above' : 'below', chartId: cell.chartId },
            indicator: {
              kind: 'horizontal',
              left: toGridX(left) + cellGutter.x,
              top: toGridY(isAbove ? cell.rect.top - cellGutter.y : cell.rect.bottom + cellGutter.y),
              width: right - left - cellGutter.x * 2,
            },
          };
        }

        const isBefore = pointer.x < left + (right - left) / 2;
        return {
          place: isBefore
            ? { kind: 'before', chartId: firstOther.chartId }
            : { kind: 'after', chartId: lastColumnOther.chartId },
          indicator: {
            kind: 'vertical',
            left: toGridX(isBefore ? left : right),
            top: toGridY(bounds.top),
            height: bounds.bottom - bounds.top,
          },
        };
      }

      // Пустое место правее последней колонки строки
      if (!lastOther) return null;
      const isSourceAloneInRow = row.columns.some(
        (item) => item.cells.length === 1 && item.cells[0].chartId === source.chartId,
      );
      const freeColumns = row.freeColumns + (isSourceAloneInRow ? source.columns : 0);
      const isFitting = freeColumns >= source.columns;
      const othersWidth = row.columns
        .filter((item) => item.cells.some((cell) => cell.chartId !== source.chartId))
        .reduce((sum, item) => {
          const { left, right } = columnBounds(item);
          return sum + right - left;
        }, 0);
      return {
        place: { kind: 'after', chartId: lastOther.chartId },
        indicator: {
          kind: 'outline',
          left: (isFitting ? othersWidth : 0) + cellGutter.x,
          top: isFitting ? toGridY(bounds.top) : toGridY(nextTop ?? bounds.bottom + cellGutter.y * 2),
          width: source.rect.width - cellGutter.x * 2,
          height: source.rect.height,
        },
      };
    }

    if (nextTop === null || pointer.y <= nextTop + ROW_GAP_CATCH) {
      if (!lastOther) return null;
      // Новая строка встаёт сразу над следующей — ниже всего, что развёрнуто под этой строкой
      const lineY = nextTop === null ? bounds.bottom + cellGutter.y : nextTop - cellGutter.y;
      return {
        place: { kind: 'newRow', afterChartId: lastOther.chartId },
        indicator: {
          kind: 'horizontal',
          left: cellGutter.x,
          top: toGridY(lineY),
          width: gridRect.width - cellGutter.x * 2,
        },
      };
    }
  }

  return null;
};
